import { useSyncExternalStore } from 'react'

export const DEFAULT_FLOATING_CHROME_INSET = 20
export const FLOATING_CHROME_LAUNCHER_CLEARANCE_PX = 10

const obstructions = new Map()
const listeners = new Set()

let cachedSnapshot = {
  right: DEFAULT_FLOATING_CHROME_INSET,
  bottom: DEFAULT_FLOATING_CHROME_INSET,
}

const getFloatingChromeInsetsUncached = () => {
  let right = DEFAULT_FLOATING_CHROME_INSET
  let bottom = DEFAULT_FLOATING_CHROME_INSET

  obstructions.forEach(({ right: obstructionRight, bottom: obstructionBottom }) => {
    if (obstructionRight > 0) {
      right = Math.max(right, obstructionRight)
    }
    if (obstructionBottom > 0) {
      bottom = Math.max(bottom, obstructionBottom)
    }
  })

  return { right, bottom }
}

const refreshSnapshot = () => {
  const next = getFloatingChromeInsetsUncached()
  if (
    cachedSnapshot.right === next.right
    && cachedSnapshot.bottom === next.bottom
  ) {
    return
  }

  cachedSnapshot = next
}

const notify = () => {
  refreshSnapshot()
  listeners.forEach((listener) => {
    listener()
  })
}

/**
 * Registers minimum viewport insets (px from right/bottom edges) required to keep
 * the Intercom launcher and other bottom-right floating chrome unobstructed.
 */
export const setFloatingChromeObstruction = (id, inset) => {
  if (inset == null) {
    if (obstructions.delete(id)) {
      notify()
    }
    return
  }

  const next = {
    right: inset.right ?? 0,
    bottom: inset.bottom ?? 0,
  }
  const prev = obstructions.get(id)

  if (prev?.right === next.right && prev?.bottom === next.bottom) {
    return
  }

  obstructions.set(id, next)
  notify()
}

export const clearFloatingChromeObstruction = (id) => {
  setFloatingChromeObstruction(id, null)
}

export const getFloatingChromeInsets = () => cachedSnapshot

export const resetFloatingChromeInsetsForTests = () => {
  obstructions.clear()
  cachedSnapshot = {
    right: DEFAULT_FLOATING_CHROME_INSET,
    bottom: DEFAULT_FLOATING_CHROME_INSET,
  }
}

const subscribe = (listener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = () => cachedSnapshot

const getServerSnapshot = () => ({
  right: DEFAULT_FLOATING_CHROME_INSET,
  bottom: DEFAULT_FLOATING_CHROME_INSET,
})

export const useFloatingChromeInsets = () => (
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
)
